import { genzoTrackImpression } from '@alexis/helpers/genzo';
import { useEffect, useState, useRef } from 'react';

import { sortFlightFares } from '@helpers/flightSort';

import type { User } from '@wegoTypes/user';

interface ImpressionLogObject {
  id: string;
  parent_id: string;
  order: number;
  order_label: string | null;
  cheapest_price_usd: number;
  clicked_price_usd: number | null;
  top_provider: string;
  clicked_provider: string | null;
}

interface tripDetailImpressionLogging {
  handleFareClicked(fareId: string): void;
  handleFilterApplied(): void;
  handlePageLoad(): void;
}

export function useTripDetailImpressionLogging(
  clientId: string | undefined,
  clientSessionId: string | undefined,
  fares: Array<FlightFareResultTripFare>,
  searchId: string | undefined,
  pageViewId: string,
  tripId: string | undefined,
  user: User | null,
): tripDetailImpressionLogging {
  const [impressionLogOnFilter, setImpressionLogOnFilter] = useState<boolean>(false);
  let filterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [impressionLogOnPageLoad, setImpressionLogOnPageLoad] = useState<boolean>(false);

  // Impression logging on page load on trip details page
  const handlePageLoad = () => {
    setTimeout(() => {
      setImpressionLogOnPageLoad(true);
    }, 1000);
  };

  // Impression Logging on flight details page
  useEffect(() => {
    if (
      !!searchId &&
      (impressionLogOnPageLoad || impressionLogOnFilter) &&
      fares.length > 0 &&
      !!tripId
    ) {
      let impressionTrigger = '';
      let clickedFare: FlightFareResultTripFare | undefined;

      if (impressionLogOnPageLoad) {
        setImpressionLogOnPageLoad(false);
        impressionTrigger = 'page_load';
      } else if (impressionLogOnFilter) {
        setImpressionLogOnFilter(false);
        impressionTrigger = 'filter';
      }

      let objects: {
        id: string;
        parent_id: string;
        order: number;
        order_label: string | null;
        cheapest_price_usd: number;
        clicked_price_usd: number | null;
        top_provider: string;
        clicked_provider: string | null;
      }[] = [];

      fares.map((fare, index) => {
        const object = {
          id: fare.id,
          parent_id: tripId,
          order: index + 1,
          order_label: fare.sponsoredListing ? 'sponsored' : null,
          cheapest_price_usd: fare.price.amountUsd,
          clicked_price_usd: clickedFare?.price.amountUsd ?? null,
          top_provider: fare.provider?.code ?? '',
          clicked_provider: clickedFare?.provider?.code ?? null,
        };

        objects.push(object);
      });

      genzoTrackImpression(
        impressionTrigger,
        API_BASE_URL,
        clientId!,
        clientSessionId!,
        null,
        objects,
        'flights_details',
        pageViewId,
        searchId,
        null,
        user?.userHash,
      );
    }
  }, [
    clientId,
    clientSessionId,
    fares,
    searchId,
    impressionLogOnPageLoad,
    impressionLogOnFilter,
    pageViewId,
    user,
  ]);

  const handleFareClicked = (fareId: string) => {
    let clickedFareIndex = 1;
    if (!!searchId && fares.length > 0 && !!tripId) {
      const clickedFare = fares.find((fare, index) => {
        if (fare.id === fareId) {
          clickedFareIndex = index + 1;
          return true;
        }
        return false;
      });

      let cheapestFare = sortFlightFares(fares, 'asc', true)[0];

      let objects: Array<ImpressionLogObject> = [];

      const object: ImpressionLogObject = {
        id: clickedFare!.id,
        parent_id: tripId,
        order: clickedFareIndex,
        order_label: clickedFare!.sponsoredListing ? 'sponsored' : null,
        cheapest_price_usd: cheapestFare?.price.amountUsd,
        clicked_price_usd: clickedFare!.price.amountUsd,
        top_provider: cheapestFare?.provider?.code ?? '',
        clicked_provider: clickedFare?.provider?.code ?? null,
      };

      objects.push(object);

      genzoTrackImpression(
        'handoff',
        API_BASE_URL,
        clientId!,
        clientSessionId!,
        null,
        objects,
        'flights_details',
        pageViewId,
        searchId,
        null,
        user?.userHash,
      );
    }
  };

  // Impression logging on filter on trip details page
  const handleFilterApplied = () => {
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    filterTimeoutRef.current = setTimeout(() => {
      setImpressionLogOnFilter(true);
    }, 1000);
  };

  const useTripDetailImpressionLogging = {
    handleFareClicked,
    handleFilterApplied,
    handlePageLoad,
  };

  return useTripDetailImpressionLogging;
}
